import {useCallback, useEffect, useState} from "react";
import {toast} from "@/components/ui/use-toast.ts";
import {TableFloatUI} from "@/components/ui/table-floatui.tsx";
import {useTranslation} from "react-i18next";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {AffinityGroup} from "@/types/AffinityGroup.ts";
import {
    createAffinityGroup,
    deleteAffinityGroup,
    getAffinityGroups,
    updateAffinityGroup,
} from "@/services/affinityGroupService.ts";

const AffinityGroupsPage = () => {
    const {t} = useTranslation();
    const [items, setItems] = useState<AffinityGroup[]>([]);
    const [itemToDelete, setItemToDelete] = useState<AffinityGroup | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const columnMap = {
        name: "Name",
        nameFr: "Name (French)",
    };

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const data = await getAffinityGroups();
                setItems(data);
            } catch (error) {
                console.error("Error fetching affinity groups:", error);
            }
        };
        fetchItems();
    }, []);

    const handleDeleteRequest = useCallback((item: AffinityGroup) => {
        setItemToDelete(item);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await deleteAffinityGroup(itemToDelete.id);
            setItems((prev) => prev.filter((item) => item.id !== itemToDelete.id));
            toast({
                title: t("common.successTitle"),
                description: t("affinity.deleteSuccess"),
            });
            setItemToDelete(null);
        } catch (error) {
            console.error("Error deleting affinity group:", error);
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: t("common.errorDesc"),
            });
        } finally {
            setIsDeleting(false);
        }
    }, [itemToDelete, t]);

    const onSave = async (item: AffinityGroup, itemId: number | null = null) => {
        try {
            if (itemId) {
                await updateAffinityGroup(itemId, item);
                toast({
                    title: t("common.successTitle"),
                    description: t("affinity.updateSuccess"),
                });
            } else {
                await createAffinityGroup(item);
                toast({
                    title: t("common.successTitle"),
                    description: t("affinity.createSuccess"),
                });
            }
            const data = await getAffinityGroups();
            setItems(data);
        } catch (error) {
            console.error("Error saving affinity group:", error);
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: t("common.errorDesc"),
            });
        }
    };

    return (
        <div className="space-y-6">
            <TableFloatUI
                tableItems={items}
                tableTitle={t("affinity.groupsTitle")}
                columns={columnMap}
                saveHandler={onSave}
                deleteHandler={handleDeleteRequest}
            />

            <AlertDialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("common.areYouSure")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("affinity.deleteConfirmDesc")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setItemToDelete(null)} disabled={isDeleting}>
                            {t("common.cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleConfirmDelete();
                            }}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AffinityGroupsPage;
